import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import cardborad from './assets/Cardboard.svg'
import bulb from './assets/bulb.svg'
import couplebulb from './assets/couplebulb.svg'
import ladyart from './assets/ladyart.svg'
import caution from './assets/caution.svg'

const features = [
  {
    id: 1,
    title: "On-Time Delivery",
    description: "We stick to deadlines. No delays, no excuses — just reliable execution.",
    image: cardborad,
  },
  {
    id: 2,
    title: "End-to-End Project Management",
    description: "From initial design to final handover, we manage every step to ensure a seamless experience.",
    image: bulb,
  },
  {
    id: 3,
    title: "Quality Craftsmanship",
    description: "Built by skilled professionals using top-grade materials and strict quality checks.",
    image: couplebulb,
  },
  {
    id: 4,
    title: "Safety First",
    description: "From initial design to final handover, we manage every step to ensure a seamless experience.",
    image: caution,
  },
  {
    id: 5,
    title: "Innovative Design & Planning",
    description: "Smart planning, modern aesthetics, and structural efficiency at the core of every project.",
    image: ladyart,
  }
];

const FeaturesSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const AUTO_PLAY_INTERVAL = 5000;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === features.length - 1 ? 0 : prev + 1));
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(nextSlide, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [nextSlide, isPlaying]);

  return (
    <section className="w-full flex justify-center px-4 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-10 bg-gray-50 ">
  
  <div className="w-full max-w-[1440px]">

    {/* Header */}
    <div className="text-center mb-6 md:mb-8 lg:mb-10">
      <span className="inline-block px-3 py-1 rounded-full bg-blue-50 text-slate-800 text-[10px] font-bold tracking-widest uppercase mb-2">
        Our Features
      </span>

      <h2 className="text-[clamp(24px,3vw,40px)] font-bold text-slate-900 mb-2">
        Our core features
      </h2>

      <p className="max-w-xl md:max-w-2xl mx-auto text-slate-500 text-[clamp(13px,1.5vw,16px)] leading-relaxed">
        Key strengths that set us apart in delivering reliable and quality construction services.
      </p>
    </div>

    {/* Slider */}
    <div className="relative min-h-[210px] md:min-h-[240px] lg:min-h-[260px] flex items-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -15 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-8 items-center w-full max-w-5xl mx-auto"
        >

          {/* Image */}
          <div className="flex justify-center order-2 md:order-1">
            <motion.img
              key={features[currentIndex].image}
              src={features[currentIndex].image}
              alt={features[currentIndex].title}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45 }}
              className="w-[65%] sm:w-[55%] md:w-[75%] lg:w-[380px] xl:w-[480px] h-auto object-contain"
            />
          </div>

          {/* Text */}
          <div className="text-center md:text-left order-1 md:order-2">
            <h3 className="text-[clamp(22px,2.5vw,32px)] font-bold text-slate-900 mb-1">
              {features[currentIndex].title}
            </h3>

            <p className="text-slate-500 text-[clamp(14px,1.4vw,18px)] leading-snug max-w-md mx-auto md:mx-0">
              {features[currentIndex].description}
            </p>
          </div>

        </motion.div>
      </AnimatePresence>
    </div>

    {/* Indicators */}
    <div className="flex justify-center items-center gap-3 mt-6">
      {features.map((_, index) => (
        <button
          key={index}
          onClick={() => setCurrentIndex(index)}
          className="relative h-1 rounded-full overflow-hidden bg-slate-200 transition-all duration-300"
          style={{ width: index === currentIndex ? '44px' : '26px' }}
        >
          {/* Past slides — solid green */}
          {index < currentIndex && (
            <div className="absolute inset-0 bg-emerald-600" />
          )}

          {/* Active slide — progress animation */}
          {index === currentIndex && (
            <motion.div
              key={`${currentIndex}-${isPlaying}`}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: isPlaying ? 1 : 0 }}
              transition={{ duration: isPlaying ? AUTO_PLAY_INTERVAL / 1000 : 0, ease: "linear" }}
              style={{ transformOrigin: 'left' }}
              className="absolute inset-0 bg-emerald-600"
            />
          )}
        </button>
      ))}

      {/* Play / Pause */}
      <button
        onClick={() => setIsPlaying(p => !p)}
        className="w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors"
      >
        {isPlaying ? (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
            <rect x="1" y="1" width="2.5" height="8" rx="0.8"/>
            <rect x="6.5" y="1" width="2.5" height="8" rx="0.8"/>
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
            <path d="M2 1l7 4-7 4z"/>
          </svg>
        )}
      </button>
    </div>

  </div>
</section>
  );
};

export default FeaturesSlider;